'use client'
import type { SelectProps } from '@pearlconvert/ui/components/select/select.types'

import classNames from 'classnames'
import { useTranslations } from 'next-intl'
import { useEffect } from 'react'

import { useSelect } from '@pearlconvert/ui/components/select/batons/use-select'
import SelectDropdown from '@pearlconvert/ui/components/select/select-dropdown'

import '@pearlconvert/ui/components/select/styles/select.scss'

export default function Select(props: SelectProps) {
  const {
    isDisabled,
    label,
    showLabel = true,
    name,
    options,
    size,
    error,
    className,
    optionalText,
  } = props
  const t = useTranslations('ui')
  const batonProps = useSelect(props)
  const selectAriaLabelKey = `select.aria-labels.${name}`
  const selectAriaLabel = t.has(selectAriaLabelKey)
    ? t(selectAriaLabelKey)
    : t('select.select-name', { name: name.toLocaleLowerCase() })

  const {
    activeIndex,
    addModal,
    getLabelDisplay,
    handleMouseClickAction,
    handleMouseLeaveOptionAction,
    handleToggleDropdown,
    isDropdownOpen,
    isDesktop,
    isMobile,
    listRef,
    modalInitialized,
    onClose,
    optionRefs,
    selectAction,
    selectButtonRef,
    selectedOption,
    setActiveIndexAction,
    setIsFocus,
    setModalInitialized,
    updateModal,
    wrapperRef,
  } = batonProps

  useEffect(() => {
    if (!isMobile) {
      return
    }
    if (!modalInitialized) {
      addModal({
        key: name,
        Component: (
          <SelectDropdown
            name={name}
            ariaLabel={selectAriaLabel}
            options={options}
            listRef={listRef as React.RefObject<HTMLUListElement>}
            isDropdownOpen={isDropdownOpen}
            selectedOption={selectedOption}
            activeIndex={activeIndex}
            setActiveIndexAction={setActiveIndexAction}
            selectAction={selectAction}
            className="select__dropdown--mobile"
            onClose={onClose}
            handleMouseClickAction={handleMouseClickAction}
            handleMouseLeaveOptionAction={handleMouseLeaveOptionAction}
            optionRefs={optionRefs}
          />
        ),
        props: {
          title: t('select.select-name', { name: label.toLocaleLowerCase() }),
          titleTag: 'h2',
          animationSide: 'bottom',
          onClose,
          scrollToTop: false,
        },
      })
      setModalInitialized(true)
    } else {
      updateModal(name, { activeIndex, selectedOption, isDropdownOpen })
    }
  }, [
    activeIndex,
    addModal,
    handleMouseClickAction,
    handleMouseLeaveOptionAction,
    isDropdownOpen,
    isMobile,
    label,
    listRef,
    modalInitialized,
    name,
    onClose,
    optionRefs,
    options,
    selectAction,
    selectAriaLabel,
    selectedOption,
    setActiveIndexAction,
    setModalInitialized,
    t,
    updateModal,
  ])

  const rootClass = classNames(
    'select__root',
    'select__box',
    {
      'select__root--has-text-optional': optionalText,
    },
    className
  )

  return (
    <div className={rootClass} ref={wrapperRef}>
      <label
        className={classNames('select__label', { 'sr-only': !showLabel })}
        id={`${name}_select_label`}
        htmlFor={`${name}_select_button`}
      >
        {label}
      </label>
      {optionalText && (
        <span className="select__label--optional">{optionalText}</span>
      )}
      <div
        className={`select__container${size ? ' select__container--' + size : ''}`}
      >
        <button
          id={`${name}_select_button`}
          ref={selectButtonRef}
          className={classNames('select__button', {
            'select__button--open': isDropdownOpen,
            error,
          })}
          type="button"
          role="combobox"
          aria-haspopup="listbox"
          aria-controls={`${name}_select_dropdown`}
          aria-labelledby={`${name}_select_label`}
          aria-expanded={isDropdownOpen}
          aria-activedescendant={
            selectedOption
              ? `${name}_select_element_${selectedOption.value}`
              : undefined
          }
          onClick={handleToggleDropdown}
          onFocus={() => setIsFocus(true)}
          onBlur={() => setIsFocus(false)}
          disabled={isDisabled}
        >
          <span className="select__button-label">{getLabelDisplay}</span>
        </button>
        {isDesktop && (
          <SelectDropdown
            name={name}
            ariaLabel={selectAriaLabel}
            options={options}
            listRef={listRef}
            isDropdownOpen={isDropdownOpen}
            selectedOption={selectedOption}
            activeIndex={activeIndex}
            setActiveIndexAction={setActiveIndexAction}
            selectAction={selectAction}
            className="select__dropdown--desktop"
            onClose={onClose}
            handleMouseClickAction={handleMouseClickAction}
            handleMouseLeaveOptionAction={handleMouseLeaveOptionAction}
            optionRefs={optionRefs}
          />
        )}
      </div>
      {error && (
        <p
          className="select__message"
          id={`error__${name}`}
          role="alert"
          aria-hidden={error === undefined}
        >
          {error}
        </p>
      )}
    </div>
  )
}
